/**
 * src/components/spatial/SpatialObjectSliceLayer.tsx
 *
 * The "slice mode" plate. Renders a thin horizontal slab at a fixed Y
 * position inside the building so the user can see a cross-section of
 * the structure. Visible ONLY when slice mode is enabled — otherwise
 * the kind filter resolves to an unmatchable expression and nothing
 * paints.
 *
 * The plate's geometry comes from the GeoLink data exactly like other
 * spatial objects (`kind === 'slice'`) — we don't synthesise it here.
 * That keeps the renderer dumb: GeoLink can ship a slice with any
 * polygon (an east-west blade, an L-shape cutting through two wings,
 * a circular core through a tower). The renderer just paints it.
 */

import React, { useEffect } from 'react';
import type { SpatialMapProvider } from '../../services/spatial/mapProvider';
import { useSpatialObjects } from './SpatialObjectsContext';
import {
  SPATIAL_LAYER_IDS,
  SPATIAL_SOURCE_ID,
  buildExtrusionBaseExpr,
  buildExtrusionColorExpr,
  buildExtrusionHeightExpr,
  buildExtrusionOpacityExpr,
  buildKindFilter,
} from './spatialLayerUtils';
import { useStrictModeSafeLayer } from './useStrictModeSafeLayer';

export interface SpatialObjectSliceLayerProps {
  provider: SpatialMapProvider;
  sourceId?: string;
  layerId?: string;
  beforeId?: string;
}

const SpatialObjectSliceLayer: React.FC<SpatialObjectSliceLayerProps> = ({
  provider,
  sourceId = SPATIAL_SOURCE_ID,
  layerId  = SPATIAL_LAYER_IDS.slice,
  beforeId,
}) => {
  const { view, filters } = useSpatialObjects();

  useStrictModeSafeLayer({
    provider,
    layerId,
    beforeId,
    buildSpec: () => ({
      id:     layerId,
      type:   'fill-extrusion',
      source: sourceId,
      filter: buildKindFilter('slice', view, filters) as unknown[],
      paint:  {
        'fill-extrusion-base':    buildExtrusionBaseExpr(view, 'slice'),
        'fill-extrusion-height':  buildExtrusionHeightExpr(view, 'slice'),
        'fill-extrusion-color':   buildExtrusionColorExpr('slice'),
        'fill-extrusion-opacity': buildExtrusionOpacityExpr('slice'),
      },
    }),
  });

  useEffect(() => {
    if (!provider.hasLayer(layerId)) return;
    provider.setFilter(layerId, buildKindFilter('slice', view, filters) as unknown[]);
    provider.setPaintProperty(layerId, 'fill-extrusion-base',   buildExtrusionBaseExpr(view, 'slice'));
    provider.setPaintProperty(layerId, 'fill-extrusion-height', buildExtrusionHeightExpr(view, 'slice'));
  }, [provider, layerId, view, filters]);

  return null;
};

export default SpatialObjectSliceLayer;
